import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../api';
import { formatAgo, formatElapsed, percent } from '../format';
import type { LiveCrossing, LiveState, Station } from '../types';
import { liveKey, useLive } from './queries';
import { AddRunnerForm } from './AddRunnerForm';
import { SetupChecklist } from './SetupChecklist';
import { useScreenWake } from '../station/session';

const KEEP_ON_KEY = 'splitline.keepScreenOn';

/** Recipe mode for the dashboard: a laptop or tablet left on the timing table shouldn't go to sleep. */
function KeepScreenOn() {
  const [on, setOn] = useState(() => {
    try {
      return localStorage.getItem(KEEP_ON_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const state = useScreenWake(on);
  const toggle = (next: boolean) => {
    setOn(next);
    try {
      localStorage.setItem(KEEP_ON_KEY, String(next));
    } catch {
      /* remembered for this visit only */
    }
  };
  return (
    <label className="check small" style={{ alignItems: 'center' }} title={on && state !== 'on' ? 'This browser won’t keep the screen on' : undefined}>
      <input type="checkbox" checked={on} onChange={(e) => toggle(e.target.checked)} />
      Keep screen on{on && (state === 'unsupported' || state === 'refused') ? ' (not supported here)' : ''}
    </label>
  );
}

/** Server time now, extrapolated from the last poll so the clock ticks smoothly. */
function useServerNow(live: LiveState | undefined) {
  const anchor = useRef<{ server: number; local: number } | null>(null);
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (live) anchor.current = { server: live.serverTimeMs, local: performance.now() };
  }, [live]);
  useEffect(() => {
    const id = setInterval(() => {
      const a = anchor.current;
      if (a) setNow(a.server + (performance.now() - a.local));
    }, 100);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** Shift the gun time when it was pressed late or early. */
function AdjustGun({ raceId, gunTimeMs }: { raceId: string; gunTimeMs: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [seconds, setSeconds] = useState('');
  const adjust = useMutation({
    mutationFn: (deltaMs: number) => api(`/races/${raceId}`, { method: 'PATCH', body: { gunTimeMs: gunTimeMs + deltaMs } }),
    onSuccess: () => {
      setOpen(false);
      setSeconds('');
      void qc.invalidateQueries({ queryKey: liveKey(raceId) });
    },
  });
  if (!open) {
    return (
      <button type="button" className="btn link small" onClick={() => setOpen(true)}>
        Adjust gun time
      </button>
    );
  }
  const delta = Number(seconds);
  return (
    <form
      className="inline"
      style={{ gap: 6 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (Number.isFinite(delta) && delta !== 0) adjust.mutate(-Math.round(delta * 1000));
      }}
    >
      <label className="small">
        Gun went off
        <input className="input mono" style={{ width: 80, minHeight: 36, margin: '0 6px' }} inputMode="decimal" placeholder="2.5" value={seconds} onChange={(e) => setSeconds(e.target.value)} autoFocus />
        seconds {Number.isFinite(delta) && delta < 0 ? 'after' : 'before'} it was pressed
      </label>
      <button type="submit" className="btn small primary" disabled={adjust.isPending || !Number.isFinite(delta) || delta === 0}>
        Apply
      </button>
      <button type="button" className="btn small" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {adjust.error && <span className="small" style={{ color: 'var(--bad-fg)' }}>{(adjust.error as Error).message}</span>}
    </form>
  );
}

function stationChip(s: Station) {
  if (!s.paired) return <span className="chip neutral">Not paired</span>;
  if (!s.online) return <span className="chip bad">Offline</span>;
  if ((s.queueDepth ?? 0) > 5) return <span className="chip warn">Uploads behind</span>;
  if (s.status === 'recording') return <span className="chip ok">Recording</span>;
  return <span className="chip info">Paired</span>;
}

const methodLabel: Record<NonNullable<LiveCrossing['assignMethod']>, string> = {
  bib: 'Bib read',
  description: 'Description match',
  llm: 'Vision model',
  manual: 'Manual',
};

function crossingChip(c: LiveCrossing) {
  if (c.status === 'review') return <span className="chip warn">Needs review</span>;
  if (c.status === 'confirmed') return <span className="chip info">Confirmed</span>;
  return <span className="chip ok">Auto</span>;
}

export function RaceDayPage() {
  const { raceId = '' } = useParams();
  const qc = useQueryClient();
  const live = useLive(raceId);
  const now = useServerNow(live.data);
  const fireGun = useMutation({
    mutationFn: () => api(`/races/${raceId}/gun`, { body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: liveKey(raceId) }),
  });

  if (live.isLoading) return <main className="page">Loading…</main>;
  if (live.error || !live.data) return <main className="page"><div className="notice bad">{(live.error as Error)?.message ?? 'Could not load the race'}</div></main>;

  const { race, timingPoints, stations, counts, processing, recentCrossings } = live.data;
  const pointName = new Map(timingPoints.map((p) => [p.id, p.name]));
  const start = timingPoints.find((p) => p.kind === 'start');
  const finish = timingPoints.find((p) => p.kind === 'finish');
  const splits = timingPoints.filter((p) => p.kind === 'split');
  const total = Object.values(counts.crossingsByPoint).reduce((a, c) => a + c.total, 0);
  const auto = Object.values(counts.crossingsByPoint).reduce((a, c) => a + c.auto + c.confirmed, 0);
  const elapsed = race.gunTimeMs && now ? now - race.gunTimeMs : null;

  return (
    <main className="page">
      <section className="page-head">
        <div>
          {race.status === 'live' && (
            <div className="inline small" style={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ok-fg)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: '#1e7a3e' }} /> Live · timing in progress
            </div>
          )}
          <h1>{race.name}</h1>
          <p>
            {race.raceDate} · {race.gunTimeMs ? `Gun at ${new Date(race.gunTimeMs).toLocaleTimeString()}` : 'Gun not fired yet'} · {counts.runners} registered · {stations.length}{' '}
            camera station{stations.length === 1 ? '' : 's'}
          </p>
          <div className="inline" style={{ gap: 16 }}>
            {race.gunTimeMs && race.status !== 'closed' && <AdjustGun raceId={raceId} gunTimeMs={race.gunTimeMs} />}
            <KeepScreenOn />
          </div>
        </div>
        <div className="inline" style={{ gap: 16 }}>
          <div className="clock">
            <span className="label">Race clock</span>
            <span className="value">{elapsed === null ? '—:—' : formatElapsed(elapsed)}</span>
          </div>
          {!race.gunTimeMs && race.status !== 'closed' ? (
            <button
              type="button"
              className="btn primary"
              disabled={fireGun.isPending}
              onClick={() => {
                if (confirm('Start the race clock now? Do this the moment the gun goes off.')) fireGun.mutate();
              }}
            >
              Fire the gun now
            </button>
          ) : (
            <Link to={`/races/${raceId}/review`} className="btn primary">
              Review {counts.reviewOpen} flagged
            </Link>
          )}
        </div>
      </section>

      <SetupChecklist raceId={raceId} live={live.data} />

      <section aria-label="Race totals" className="grid-tiles">
        {start && (
          <div className="card tile">
            <div className="label">Started</div>
            <div className="value">
              {counts.crossingsByPoint[start.id]?.total ?? 0} <small>/ {counts.runners}</small>
            </div>
          </div>
        )}
        {splits.map((p) => (
          <div className="card tile" key={p.id}>
            <div className="label">Through {p.name}</div>
            <div className="value">{counts.crossingsByPoint[p.id]?.total ?? 0}</div>
          </div>
        ))}
        {finish && (
          <div className="card tile">
            <div className="label">Finished</div>
            <div className="value">{counts.crossingsByPoint[finish.id]?.total ?? 0}</div>
          </div>
        )}
        <div className="card tile">
          <div className="label">Auto-confirmed</div>
          <div className="value">{total ? percent(auto / total) : '—'}</div>
          <div className="small muted">of crossings, no human needed</div>
        </div>
        <div className={`card tile ${counts.reviewOpen ? 'hot' : ''}`}>
          <div className="label">Needs review</div>
          <div className="value">{counts.reviewOpen}</div>
        </div>
      </section>

      <section className="stack">
        <div className="card-head" style={{ margin: 0 }}>
          <h2 className="section-title">Camera stations</h2>
          <Link to={`/races/${raceId}/course`} style={{ fontWeight: 600 }}>
            Manage stations
          </Link>
        </div>
        {stations.length === 0 && <div className="card empty">No stations yet. Add timing points and pair phones on the Course &amp; stations page.</div>}
        <div className="grid-cards">
          {stations.map((s) => (
            <div className="card stack" key={s.id} style={{ gap: 10 }}>
              <div className="inline" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div className="display" style={{ fontSize: 20 }}>
                    {s.label}
                  </div>
                  <div className="small muted">
                    {pointName.get(s.timingPointId)} · {s.deviceLabel ?? 'no device'}
                    {s.volunteerName ? ` · ${s.volunteerName}` : ''}
                  </div>
                </div>
                {stationChip(s)}
              </div>
              <dl className="kv" style={{ margin: 0 }}>
                <dt>Last check-in</dt>
                <dd>{s.lastHeartbeatMs && now ? formatAgo(now - s.lastHeartbeatMs) : '—'}</dd>
                <dt>Clock offset</dt>
                <dd>{s.clockRttMs !== null ? `±${Math.round(s.clockRttMs / 2)} ms` : '—'}</dd>
                <dt>Uploads waiting</dt>
                <dd>{s.queueDepth ?? '—'}</dd>
                <dt>Battery</dt>
                <dd>
                  {percent(s.battery)}
                  {s.charging ? ' · charging' : ''}
                </dd>
                <dt>Line</dt>
                <dd>{s.line ? 'set' : 'not set'}</dd>
              </dl>
            </div>
          ))}
        </div>
      </section>

      <div className="row">
        <section className="card flush main-col" style={{ gap: 0 }}>
          <div className="card-head">
            <h2>Live crossings</h2>
            <span className="small muted">Newest first · gun time</span>
          </div>
          {recentCrossings.length === 0 ? (
            <p className="empty">No crossings yet. They appear here as the cameras process video.</p>
          ) : (
            <div className="table-wrap">
              <table className="data" style={{ minWidth: 720 }}>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Bib</th>
                    <th>Runner</th>
                    <th>Point</th>
                    <th>Identified by</th>
                    <th className="num">Conf.</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCrossings.map((c) => (
                    <tr key={c.id}>
                      <td className="mono">{race.gunTimeMs ? formatElapsed(c.timeMs - race.gunTimeMs) : new Date(c.timeMs).toLocaleTimeString()}</td>
                      <td>
                        <span className="bib">{c.runner?.bib ?? '?'}</span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.runner?.name ?? 'Unknown runner'}</div>
                        <div className="small muted">{c.runner?.division ?? ''}</div>
                      </td>
                      <td>{pointName.get(c.timingPointId)}</td>
                      <td>{c.assignMethod ? methodLabel[c.assignMethod] : '—'}</td>
                      <td className="num mono">{c.confidence?.toFixed(2) ?? '—'}</td>
                      <td>{crossingChip(c)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="side-col">
          {race.status !== 'closed' && (
            <section className="card stack">
              <h2>Add a runner</h2>
              <p className="small muted" style={{ margin: 0 }}>
                Walk-ups and race-day entries. Add them before they cross; anyone added after crossing shows up in Needs review to assign.
              </p>
              <AddRunnerForm raceId={raceId} stacked />
            </section>
          )}
          <section className="card stack">
            <h2>Processing</h2>
            <dl className="kv" style={{ margin: 0, fontSize: 14 }}>
              <dt>Video chunks received</dt>
              <dd>{processing.chunksUploaded}</dd>
              <dt>Waiting to process</dt>
              <dd>{processing.backlog}</dd>
              <dt>Failed</dt>
              <dd>{processing.failed}</dd>
            </dl>
            {processing.backlog > 20 && <div className="notice warn">Processing is behind. Results will catch up; nothing is lost.</div>}
          </section>
        </aside>
      </div>
    </main>
  );
}
